<template lang="pug">
v-container(fluid)
  v-row
    v-col(cols="12")
      v-card
        v-card-title
          v-icon(left, color="primary") mdi-account-multiple
          | Gestión Global de Usuarios
          v-spacer
          v-btn(
            color="primary"
            @click="openUserForm()"
            :loading="savingUser"
          )
            v-icon(left) mdi-plus
            | Nuevo Usuario

        v-card-text
          // Filtros
          v-row
            v-col(cols="12", md="3")
              v-text-field(
                v-model="search"
                label="Buscar usuario..."
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined
                dense
              )
            v-col(cols="12", md="3")
              v-select(
                v-model="restaurantFilter"
                :items="restaurantOptions"
                label="Filtrar por restaurante"
                clearable
                outlined
                dense
              )
            v-col(cols="12", md="3")
              v-select(
                v-model="roleFilter"
                :items="roleOptions"
                label="Filtrar por rol"
                clearable
                outlined
                dense
              )
            v-col(cols="12", md="3")
              v-select(
                v-model="statusFilter"
                :items="statusOptions"
                label="Filtrar por estado"
                clearable
                outlined
                dense
              )

          // Tabla de usuarios
          v-data-table(
            :headers="userHeaders"
            :items="filteredUsers"
            :loading="loadingUsers"
            :search="search"
            loading-text="Cargando usuarios..."
            no-data-text="No hay usuarios registrados"
            :items-per-page="10"
            :footer-props="{ itemsPerPageText: 'Usuarios por página:', itemsPerPageAllText: 'Todos' }"
          )
            template(v-slot:item.photo="{ item }")
              v-avatar(size="40")
                v-img(v-if="item.photo" :src="item.photo")
                v-icon(v-else, color="grey") mdi-account

            template(v-slot:item.restaurant="{ item }")
              v-chip(
                v-if="item.restaurant"
                small
                :color="getRestaurantColor(item.restaurant.name)"
                text-color="white"
              )
                v-avatar(left, size="20")
                  v-img(v-if="item.restaurant.logo" :src="item.restaurant.logo")
                  v-icon(v-else, small) mdi-store
                | {{ item.restaurant.name }}
              span(v-else) Sin restaurante

            template(v-slot:item.role="{ item }")
              v-chip(
                v-if="item.role"
                small
                :color="getRoleColor(item.role.name)"
                text-color="white"
              ) {{ item.role.name }}
              span(v-else) Sin rol

            template(v-slot:item.active="{ item }")
              v-chip(
                small
                :color="item.active ? 'success' : 'error'"
                text-color="white"
              )
                v-icon(left, small) {{ item.active ? 'mdi-check' : 'mdi-close' }}
                | {{ item.active ? 'Activo' : 'Inactivo' }}

            template(v-slot:item.actions="{ item }")
              v-tooltip(bottom)
                template(v-slot:activator="{ on, attrs }")
                  v-btn(
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openUserForm(item)"
                  )
                    v-icon mdi-pencil
                span Editar usuario

              v-tooltip(bottom)
                template(v-slot:activator="{ on, attrs }")
                  v-btn(
                    icon
                    small
                    :color="item.active ? 'error' : 'success'"
                    v-bind="attrs"
                    v-on="on"
                    @click="toggleUserStatus(item)"
                  )
                    v-icon {{ item.active ? 'mdi-account-off' : 'mdi-account-check' }}
                span {{ item.active ? 'Desactivar' : 'Activar' }} usuario

  // Diálogo para crear/editar usuario
  v-dialog(v-model="userFormDialog" max-width="600px" persistent)
    v-card
      v-card-title
        v-icon(left, color="primary") {{ editedUser._id ? 'mdi-pencil' : 'mdi-plus' }}
        | {{ editedUser._id ? 'Editar Usuario' : 'Nuevo Usuario' }}
        v-spacer
        v-btn(icon @click="closeUserForm")
          v-icon mdi-close

      v-card-text
        v-form(ref="userForm" v-model="userFormValid" lazy-validation)
          v-row
            v-col(cols="12", md="6")
              v-text-field(
                v-model="editedUser.name"
                label="Nombre completo *"
                :rules="nameRules"
                outlined
                required
              )
            v-col(cols="12", md="6")
              v-text-field(
                v-model="editedUser.email"
                label="Email *"
                :rules="emailRules"
                outlined
                required
                type="email"
              )
            v-col(cols="12", md="6")
              v-text-field(
                v-model="editedUser.phone"
                label="Teléfono"
                :rules="phoneRules"
                outlined
              )
            v-col(cols="12", md="6")
              v-text-field(
                v-model="editedUser.dni"
                label="DNI"
                outlined
              )
            v-col(cols="12", md="6")
              v-select(
                v-model="editedUser.restaurant"
                :items="restaurantOptions"
                label="Restaurante *"
                :rules="restaurantRules"
                outlined
                required
              )
            v-col(cols="12", md="6")
              v-select(
                v-model="editedUser.role"
                :items="availableRolesForSelect"
                item-text="text"
                item-value="_id"
                label="Rol *"
                :rules="roleRules"
                outlined
                required
              )
            v-col(cols="12", md="6")
              v-text-field(
                v-model="editedUser.password"
                :label="editedUser._id ? 'Nueva contraseña (opcional)' : 'Contraseña *'"
                :rules="editedUser._id ? [] : passwordRules"
                outlined
                type="password"
                :required="!editedUser._id"
              )
            v-col(cols="12", md="6")
              v-switch(
                v-model="editedUser.active"
                label="Usuario activo"
                color="success"
              )

      v-card-actions
        v-spacer
        v-btn(text @click="closeUserForm") Cancelar
        v-btn(
          color="primary"
          :loading="savingUser"
          @click="saveUser"
        ) {{ editedUser._id ? 'Actualizar' : 'Crear' }} Usuario
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'UsersManager',
  
  data: () => ({
    loadingUsers: false,
    savingUser: false,
    search: '',
    restaurantFilter: null,
    roleFilter: null,
    statusFilter: null,
    userFormDialog: false,
    userFormValid: false,
    allUsers: [],
    editedUser: {
      _id: null,
      name: '',
      email: '',
      phone: '',
      dni: '',
      restaurant: null,
      role: null,
      password: '',
      active: true
    },
    defaultUser: {
      _id: null,
      name: '',
      email: '',
      phone: '',
      dni: '',
      restaurant: null,
      role: null,
      password: '',
      active: true
    },
    userHeaders: [
      { text: 'Foto', value: 'photo', sortable: false },
      { text: 'Nombre', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Teléfono', value: 'phone' },
      { text: 'Restaurante', value: 'restaurant', sortable: false },
      { text: 'Rol', value: 'role', sortable: false },
      { text: 'Estado', value: 'active', sortable: false },
      { text: 'Acciones', value: 'actions', sortable: false }
    ],
    statusOptions: [
      { text: 'Activos', value: true },
      { text: 'Inactivos', value: false }
    ],
    nameRules: [
      v => !!v || 'El nombre es requerido'
    ],
    emailRules: [
      v => !!v || 'El email es requerido',
      v => /.+@.+\..+/.test(v) || 'Email inválido'
    ],
    phoneRules: [
      v => !v || /^\+?[\d\s-]+$/.test(v) || 'Teléfono inválido'
    ],
    restaurantRules: [
      v => !!v || 'El restaurante es requerido'
    ],
    roleRules: [
      v => !!v || 'El rol es requerido'
    ],
    passwordRules: [
      v => !!v || 'La contraseña es requerida',
      v => v.length >= 8 || 'La contraseña debe tener al menos 8 caracteres'
    ]
  }),

  computed: {
    ...mapState({
      rolesState: state => state.roles.list
    }),
    
    restaurants() {
      return this.$store.state.restaurant.list
    },

    // Lista de roles robusta desde getters/estado
    roles() {
      const fromGetter = this.$store.getters['roles/allRolesWithStatus']
      const fromState = Array.isArray(this.rolesState) ? this.rolesState : []
      const list = Array.isArray(fromGetter) ? fromGetter : fromState
      return list.filter(r => r && r.isActive !== false)
    },
    availableRoles() {
      const list = Array.isArray(this.roles) ? this.roles : []
      // Excluir propietario considerando posibles campos de nombre
      return list.filter(role => {
        const name = role?.name || role?.code || role?.key || ''
        return name !== 'owner'
      })
    },
    availableRolesForSelect() {
      const list = Array.isArray(this.availableRoles) ? this.availableRoles : []
      const labels = {
        'admin': 'Administrador',
        'customer': 'Cliente',
        'employee': 'Empleado',
        'manager': 'Encargado',
        'owner': 'Dueño',
        'waiter': 'Mozo/a',
        'courier': 'Repartidor/a',
        'chef': 'Chef',
        'kitchen': 'Cocina',
        'cashier': 'Cajero/a',
        'delivery': 'Repartidor/a',
        'host': 'Recepcionista',
        'barman': 'Barman',
        'dishwasher': 'Lavacopas',
        'supervisor': 'Supervisor/a',
        'quality': 'Control de Calidad',
        'security': 'Seguridad',
        'maintenance': 'Mantenimiento',
        'accounting': 'Contabilidad',
        'marketing': 'Marketing',
        'hr': 'Recursos Humanos',
        'purchasing': 'Compras',
        'inventory': 'Inventario',
        'receptionist': 'Recepcionista',
        'sous_chef': 'Segundo Chef',
        'bartender': 'Bartender'
      }
      return list.map(role => {
        const name = role?.name || role?.code || role?.key || ''
        const display = role?.displayName || labels[name] || (name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Rol')
        return { ...role, text: display }
      })
    },
    restaurantOptions() {
      return this.restaurants.map(restaurant => ({
        text: restaurant.name,
        value: restaurant._id
      }))
    },
    roleOptions() {
      const list = Array.isArray(this.availableRoles) ? this.availableRoles : []
      return list.map(role => {
        const name = role?.name || role?.code || role?.key || ''
        const labels = {
          'admin': 'Administrador',
          'customer': 'Cliente',
          'employee': 'Empleado',
          'manager': 'Encargado',
          'owner': 'Dueño',
          'waiter': 'Mozo/a',
          'courier': 'Repartidor/a',
          'chef': 'Chef',
          'kitchen': 'Cocina',
          'cashier': 'Cajero/a',
          'delivery': 'Repartidor/a',
          'host': 'Recepcionista',
          'barman': 'Barman',
          'dishwasher': 'Lavacopas',
          'supervisor': 'Supervisor/a',
          'quality': 'Control de Calidad',
          'security': 'Seguridad',
          'maintenance': 'Mantenimiento',
          'accounting': 'Contabilidad',
          'marketing': 'Marketing',
          'hr': 'Recursos Humanos',
          'purchasing': 'Compras',
          'inventory': 'Inventario',
          'receptionist': 'Recepcionista',
          'sous_chef': 'Segundo Chef',
          'bartender': 'Bartender'
        }
        const label = role?.displayName || role?.label || labels[name] || (name ? name.charAt(0).toUpperCase() + name.slice(1) : 'Rol')
        return { text: label, value: name }
      })
    },

    filteredUsers() {
      let filtered = this.allUsers

      // Filtrar por restaurante
      if (this.restaurantFilter) {
        filtered = filtered.filter(user => 
          user.restaurant && user.restaurant._id === this.restaurantFilter
        )
      }

      // Filtrar por rol
      if (this.roleFilter) {
        filtered = filtered.filter(user => 
          user.role && user.role.name === this.roleFilter
        )
      }

      // Filtrar por estado
      if (this.statusFilter !== null) {
        filtered = filtered.filter(user => user.active === this.statusFilter)
      }

      return filtered
    }
  },

  methods: {
    async loadAllUsers() {
      this.loadingUsers = true
      try {
        // Cargar todos los usuarios del sistema usando el store
        const users = await this.$store.dispatch('users/fetchAll')
        this.allUsers = users
      } catch (error) {
        console.error('Error cargando usuarios:', error)
        this.$toast.error('Error al cargar usuarios del sistema')
        this.allUsers = []
      } finally {
        this.loadingUsers = false
      }
    },

    openUserForm(user = null) {
      if (user) {
        // Editar usuario existente
        this.editedUser = {
          ...user,
          restaurant: user.restaurant?._id || null,
          role: user.role?._id || null,
          password: '' // No mostrar la contraseña actual
        }
      } else {
        // Nuevo usuario
        this.editedUser = { ...this.defaultUser }
      }
      this.userFormDialog = true
    },

    closeUserForm() {
      this.userFormDialog = false
      this.editedUser = { ...this.defaultUser }
      this.$nextTick(() => {
        if (this.$refs.userForm) {
          this.$refs.userForm.resetValidation()
        }
      })
    },

    async saveUser() {
      if (!this.$refs.userForm.validate()) return

      this.savingUser = true
      try {
        const userData = { ...this.editedUser }

        // Si no hay contraseña y es edición, no enviar el campo password
        if (userData._id && !userData.password) {
          delete userData.password
        }

        if (userData._id) {
          // Actualizar usuario existente
          await this.$store.dispatch('users/update', userData)
          this.$toast.success('Usuario actualizado correctamente')
        } else {
          // Crear nuevo usuario
          await this.$store.dispatch('users/create', userData)
          this.$toast.success('Usuario creado correctamente')
        }

        // Recargar la lista de usuarios
        await this.loadAllUsers()
        this.closeUserForm()
      } catch (error) {
        console.error('Error guardando usuario:', error)
        this.$toast.error('Error al guardar usuario: ' + (error.message || 'Error desconocido'))
      } finally {
        this.savingUser = false
      }
    },

    async toggleUserStatus(user) {
      try {
        if (user.active) {
          // Desactivar usuario
          await this.$store.dispatch('users/deactivate', user._id)
          this.$toast.success(`Usuario ${user.name} desactivado`)
        } else {
          // Reactivar usuario
          await this.$store.dispatch('users/update', {
            ...user,
            restaurant: user.restaurant?._id,
            role: user.role?._id,
            active: true
          })
          this.$toast.success(`Usuario ${user.name} reactivado`)
        }
        
        // Recargar la lista de usuarios
        await this.loadAllUsers()
      } catch (error) {
        console.error('Error cambiando estado del usuario:', error)
        this.$toast.error('Error al cambiar el estado del usuario')
      }
    },

    getRoleColor(roleName) {
      const roleColors = {
        'admin': 'primary',
        'manager': 'success',
        'employee': 'info',
        'waiter': 'warning',
        'chef': 'orange',
        'cashier': 'purple'
      }
      return roleColors[roleName] || 'grey'
    },

    getRestaurantColor(restaurantName) {
      // Generar color basado en el hash del nombre del restaurante
      const colors = ['primary', 'success', 'info', 'warning', 'error', 'purple', 'indigo', 'teal']
      const hash = restaurantName.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0)
        return a & a
      }, 0)
      return colors[Math.abs(hash) % colors.length]
    }
  },

  async created() {
    try {
      this.loadingUsers = true
      await Promise.all([
        this.$store.dispatch('restaurant/fetchAllForAdmin'),
        this.$store.dispatch('roles/fetchRestaurantCustomizations'),
        this.loadAllUsers()
      ])
    } catch (error) {
      this.$toast.error('Error al cargar los datos')
    } finally {
      this.loadingUsers = false
    }
  }
}
</script>

<style scoped>
.v-chip {
  margin: 2px;
}
</style>
